import { devtools, persist } from 'zustand/middleware';

import { type BaseCurrencyType } from '@pkg/types';
import { formatCrypto, formatUsd } from '@pkg/utils';

import { create } from '@app/store/zustand/helpers/create';

type GameCurrencies = {
    [gameSlug: string]: BaseCurrencyType;
};

export interface BaseCurrencyData {
    currency: BaseCurrencyType;
    defaultCurrency: BaseCurrencyType;
    gameCurrencies: GameCurrencies;
}

export interface BaseCurrencyState extends BaseCurrencyData {
    setCurrency: (currency: BaseCurrencyType, preventDefaultSet?: boolean) => void;
    setDefault: (currency: BaseCurrencyType) => void;
    setGameCurrency: (currency: BaseCurrencyType, gameSlug: string) => void;
    formatCurrency: (currency: BaseCurrencyType, value?: number, stripZeroDecimal?: boolean) => string;
}

const initialState: BaseCurrencyData = {
    currency: 'usd',
    defaultCurrency: 'usd',
    gameCurrencies: {},
};

export const STORE_KEY_BASE_CURRENCY = 'base-currency-store';

export const useBaseCurrencyStore = create<BaseCurrencyState>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,
                setCurrency: (currency, preventDefaultSet) =>
                    set((state) => {
                        const defaultCurrency = preventDefaultSet ? state.defaultCurrency : currency;
                        return { currency, defaultCurrency };
                    }),
                setDefault: (currency) => set({ defaultCurrency: currency }),
                setGameCurrency: (currency, gameSlug) =>
                    set((state) => {
                        return { gameCurrencies: { ...state.gameCurrencies, [gameSlug]: currency } };
                    }),
                formatCurrency: (currency, value = 0, stripZeroDecimal = false) => {
                    return currency === 'usd' ? formatUsd(value, stripZeroDecimal) : formatCrypto(value);
                },
            }),
            { name: STORE_KEY_BASE_CURRENCY },
        ),
    ),
);

export function getFormattedCurrency(currency, value = 0, stripZeroDecimal = false) {
    return useBaseCurrencyStore.getState().formatCurrency(currency, value, stripZeroDecimal);
}
